import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    showAttributes = "showAttributes",
    headingTitle = "headingTitle",
    noOfAttributes = "noOfAttributes",
}

type Props = WidgetProps & HasProduct;

export interface ProductDetailsSpecificationsStyles {
    headingTitle?: TypographyPresentationProps;
    attributeTypeLabelText?: TypographyPresentationProps;
    attributeTypeValuesText?: TypographyPresentationProps;
    attributeLabelWrapper?: InjectableCss;
    attributeValueWrapper?: InjectableCss;
    attributesContainer?: InjectableCss;
}

export const specificationsStyles: ProductDetailsSpecificationsStyles = {
    headingTitle: {
        variant: "h3",
        color: "text.accent",
        css: css`
            margin-bottom: 15px;
            font-weight: 600;
            font-size: 1.4rem;
        `,
    },
    attributeTypeLabelText: {
        variant: "headerSecondary",
        color: "text.accent",
        css: css`
            text-transform: capitalize;
        `,
    },
    attributeTypeValuesText: {
        variant: "headerTertiary",
        color: "text.accent",
        weight: 400,
        css: css`
            text-transform: capitalize;
        `,
    },
    attributeLabelWrapper: {
        css: css`
            background: white;
            padding: 9px 15px;
        `,
    },
    attributeValueWrapper: {
        css: css`
            background: white;
            padding: 9px 15px;
        `,
    },
    attributesContainer: {
        css: css`
            display: grid;
            grid-template-columns: 1fr 2fr;
            gap: 1px;
            background: ${getColor("common.border")};
            border: 1px solid ${getColor("common.border")};
        `,
    },
};

const styles = specificationsStyles;

const ProductDetailsKeyFeatures: React.FC<Props> = ({
    product: { brand, attributeTypes },
    fields: { headingTitle, noOfAttributes },
}) => {
    const renderingAttributes = brand || (attributeTypes && attributeTypes.length > 0);
    const attributesLimit = Number(noOfAttributes);
    const displayedAttributeTypes = attributesLimit > 0 ? attributeTypes?.slice(0, attributesLimit) : attributeTypes;

    if (!renderingAttributes) {
        return null;
    }

    return (<>
        {headingTitle && <Typography {...styles.headingTitle}>{headingTitle}</Typography>}
        <StyledWrapper {...styles.attributesContainer}>
            {brand && (
                <>
                    <StyledWrapper {...styles.attributeLabelWrapper}>
                        <Typography {...styles.attributeTypeLabelText}>{translate("Brand")}</Typography>
                    </StyledWrapper>
                    <StyledWrapper {...styles.attributeValueWrapper}>
                        <Typography {...styles.attributeTypeValuesText}>{brand.name}</Typography>
                    </StyledWrapper>
                </>
            )}
            {displayedAttributeTypes?.map(attributeType => (
                <React.Fragment key={attributeType.id}>
                    <StyledWrapper {...styles.attributeLabelWrapper}>
                        <Typography {...styles.attributeTypeLabelText}>{attributeType.label || attributeType.name}</Typography>
                    </StyledWrapper>
                    <StyledWrapper {...styles.attributeValueWrapper}>
                        <Typography {...styles.attributeTypeValuesText}>
                            {attributeType.attributeValues?.map(attributeValue => attributeValue.valueDisplay).join(", ")}
                        </Typography>
                    </StyledWrapper>
                </React.Fragment>
            ))}
        </StyledWrapper>
    </>);
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsKeyFeatures),
    definition: {
        displayName: "Key Features",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.showAttributes,
                displayName: "Show Attributes",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.headingTitle,
                displayName: "Heading Title",
                editorTemplate: "TextField",
                defaultValue: "Key Features",
                fieldType: "General",
            },
            {
                name: fields.noOfAttributes,
                displayName: "Number of Attributes",
                editorTemplate: "TextField",
                defaultValue: "0",
                fieldType: "General",
            }
        ],
    },
};

export default widgetModule;
